import { useEffect, useState } from "react";
import { SearchType } from "Dashboard/domain/SearchType";
import { useAppDispatch } from "hooks/hooks";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import hrsClasses from "../../../styles/popover.module.scss";
import classes from "../../../../../../components/Popover/popover.module.scss";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { styles } from "components/Popover/popoverSelectStyle";
import { IGetBuildingsListRequestParams } from "Dashboard/scenes/HighRiseSales/domain/IGetBuildingsListRequestParams";
import { getBuildingsList, setSearchHRSPopover } from "../main.slice";
import MunicipalitySelect from "Dashboard/scenes/Partners/components/MunicipalitySelect";
import DatePicker from "react-datepicker";
import { FormatDateIso } from "utils/dateUtils";

interface IProps {
  handleSubmit: UseFormHandleSubmit<IGetBuildingsListRequestParams>;
  register: UseFormRegister<IGetBuildingsListRequestParams>;
  control: Control<IGetBuildingsListRequestParams, any>;
  getValues: UseFormGetValues<IGetBuildingsListRequestParams>;
  searchType: SearchType;
  setSearchType: React.Dispatch<React.SetStateAction<SearchType>>;
  setFocus: UseFormSetFocus<IGetBuildingsListRequestParams>;
  setValue: UseFormSetValue<IGetBuildingsListRequestParams>;
};

const SearchHighRiseSalesPopover = (props: IProps) => {
  const dispatch = useAppDispatch();
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setFocus,
    searchType,
    setSearchType,
    setValue
  } = props;

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    [getValues("contractDateFrom") ? new Date(getValues("contractDateFrom")!) : null,
    getValues("contractDateTo") ? new Date(getValues("contractDateTo")!) : null]);

  const [startDateRange, setStartDateRange] = useState<[Date | null, Date | null]>(
    [getValues("startDateFrom") ? new Date(getValues("startDateFrom")!) : null,
    getValues("startDateTo") ? new Date(getValues("startDateTo")!) : null]);

  const [dueDateRange, setDueDateRange] = useState<[Date | null, Date | null]>(
    [getValues("dueDateFrom") ? new Date(getValues("dueDateFrom")!) : null,
    getValues("dueDateTo") ? new Date(getValues("dueDateTo")!) : null]);

  const [finishDateRange, setFinishDateRange] = useState<[Date | null, Date | null]>(
    [getValues("finishDateFrom") ? new Date(getValues("finishDateFrom")!) : null,
    getValues("finishDateTo") ? new Date(getValues("finishDateTo")!) : null]);

  const close = () => {
    dispatch(setSearchHRSPopover({ isOpen: false }));
  };

  const switchSearchMode = (searchType: SearchType) => {
    setSearchType(searchType);
  };

  const onSubmit = async (data: IGetBuildingsListRequestParams) => {
    try {
      if (props.searchType === SearchType.BASIC) {
        data = { searchTerm: data.searchTerm };
      } else {
        data.searchTerm = undefined;
      }
      let newFilter = {
        searchTerm: data.searchTerm,
        name: data.name,
        investorId: data.investorId,
        municipalityId: data.municipalityId,
        contractDateFrom: data.contractDateFrom,
        contractDateTo: data.contractDateTo,
        dueDateFrom: data.dueDateFrom,
        dueDateTo: data.dueDateTo,
        startDateFrom: data.startDateFrom,
        startDateTo: data.startDateTo,
        finishDateFrom: data.finishDateFrom,
        finishDateTo: data.finishDateTo
      };
      dispatch(getBuildingsList(newFilter));
      close();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (searchType === SearchType.BASIC) {
      setFocus("searchTerm");
    } else {
      setFocus("name");
    }
  }, [setFocus, searchType]);


  return (
    <form
      className={`${classes.basicPopover} ${hrsClasses.searchPopover}`}
      onSubmit={handleSubmit(onSubmit)}
    >
      {searchType === SearchType.BASIC && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.colDir}`}>
              <input
                type="text"
                className={`${classes.fullWidth} ${classes.itemBorder}`}
                {...register("searchTerm")}
              />
            </div>
            <div className={classes.gPopoverDisclaimer}>
              * možete uneti više reči tj. kriterijuma pretrage
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.ADVANCED)}
                className={classes.clickableText}
              >
                NAPREDNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
      {searchType === SearchType.ADVANCED && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>naziv</label>
              <input type="text" className={classes.itemBorder} {...register("name")} />
            </div>
          </div>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>mesto</label>
              <Controller
                name="municipalityId"
                control={control}
                defaultValue={undefined}
                rules={{ required: false }}
                render={({ field: { onChange } }) => (
                  <MunicipalitySelect
                    value={getValues("municipalityId")}
                    customStyles={styles.customStyleClassic}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>datum ugovora od do</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setDateRange([date[0], date[1]]);
                    setValue("contractDateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("contractDateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>početak radova od do</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={startDateRange[0]}
                  endDate={startDateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setStartDateRange([date[0], date[1]]);
                    setValue("startDateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("startDateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>krajnji rok od do</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={dueDateRange[0]}
                  endDate={dueDateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setDueDateRange([date[0], date[1]]);
                    setValue("dueDateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("dueDateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>završio od do</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={finishDateRange[0]}
                  endDate={finishDateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setFinishDateRange([date[0], date[1]]);
                    setValue("finishDateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("finishDateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.BASIC)}
                className={classes.clickableText}
              >
                PAMETNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
    </form>
  );
};

export default SearchHighRiseSalesPopover;
